import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Slider, Typography } from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import { clampTransform, computeScaledImageDimensions } from './polaroidCanvas';
export const PREVIEW_MAX_WIDTH = 360;

function formatDimMm(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

const dimLabelSx = {
  fontSize: '0.65rem',
  color: 'text.secondary',
  lineHeight: 1,
  userSelect: 'none',
};

const dimLineSx = {
  bgcolor: 'text.disabled',
  flexShrink: 0,
};

export default function PolaroidEditor({
  imageSrc,
  size,
  transform,
  onTransformChange,
}) {
  const { translate } = useTranslation();
  const frameRef = useRef(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const cardAspect = size ? size.width_mm / size.height_mm : 1;
  const previewWidth = PREVIEW_MAX_WIDTH;
  const previewHeight = previewWidth / cardAspect;
  const scaleFactor = size ? previewWidth / size.width_mm : 1;

  const imageAreaStyle = size
    ? {
        left: `${size.image_left_mm * scaleFactor}px`,
        top: `${size.image_top_mm * scaleFactor}px`,
        width: `${size.image_width_mm * scaleFactor}px`,
        height: `${size.image_height_mm * scaleFactor}px`,
      }
    : {};

  useEffect(() => {
    if (!imageSrc) {
      setImageSize({ w: 0, h: 0 });
      setImageLoaded(false);
      return undefined;
    }
    const img = new Image();
    if (imageSrc && !imageSrc.startsWith('blob:') && !imageSrc.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
      setImageLoaded(true);
    };
    img.src = imageSrc;
    return undefined;
  }, [imageSrc]);

  const areaW = size ? size.image_width_mm * scaleFactor : 0;
  const areaH = size ? size.image_height_mm * scaleFactor : 0;

  useEffect(() => {
    if (!imageLoaded || !onTransformChange || !transform || !imageSize.w || !imageSize.h) {
      return undefined;
    }
    const clamped = clampTransform(transform, imageSize.w, imageSize.h, areaW, areaH);
    if (
      clamped.offsetX !== (transform.offsetX ?? 0)
      || clamped.offsetY !== (transform.offsetY ?? 0)
    ) {
      onTransformChange(clamped);
    }
    return undefined;
  }, [imageLoaded, imageSize, areaW, areaH, transform, onTransformChange]);

  const clampedTransform = imageLoaded && imageSize.w && imageSize.h
    ? clampTransform(transform, imageSize.w, imageSize.h, areaW, areaH)
    : transform;
  const { effectiveScale } = imageLoaded && imageSize.w && imageSize.h
    ? computeScaledImageDimensions(
      imageSize.w,
      imageSize.h,
      areaW,
      areaH,
      clampedTransform?.userScale ?? 1,
    )
    : { effectiveScale: 1 };
  const offsetX = clampedTransform?.offsetX ?? 0;
  const offsetY = clampedTransform?.offsetY ?? 0;

  const handlePointerDown = useCallback((event) => {
    if (!transform) return;
    draggingRef.current = true;
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      offsetX: transform.offsetX ?? 0,
      offsetY: transform.offsetY ?? 0,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [transform]);

  const handlePointerMove = useCallback((event) => {
    if (!draggingRef.current || !onTransformChange || !transform || !imageLoaded) return;
    const dx = event.clientX - dragStartRef.current.x;
    const dy = event.clientY - dragStartRef.current.y;
    onTransformChange(clampTransform(
      {
        ...transform,
        offsetX: dragStartRef.current.offsetX + dx,
        offsetY: dragStartRef.current.offsetY + dy,
      },
      imageSize.w,
      imageSize.h,
      areaW,
      areaH,
    ));
  }, [onTransformChange, transform, imageLoaded, imageSize, areaW, areaH]);

  const handlePointerUp = useCallback((event) => {
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleZoomChange = useCallback((_event, value) => {
    if (!onTransformChange || !transform || !imageLoaded) return;
    onTransformChange(clampTransform(
      { ...transform, userScale: value },
      imageSize.w,
      imageSize.h,
      areaW,
      areaH,
    ));
  }, [onTransformChange, transform, imageLoaded, imageSize, areaW, areaH]);

  if (!size || !imageSrc) {
    return null;
  }

  const imgDisplayW = imageSize.w * effectiveScale;
  const imgDisplayH = imageSize.h * effectiveScale;

  const widthLabel = `${formatDimMm(size.width_mm)} mm`;
  const heightLabel = `${formatDimMm(size.height_mm)} mm`;

  return (
    <Box>
      <Box sx={{ width: 'fit-content', mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.5,
            pl: 3.5,
            width: previewWidth + 28,
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ ...dimLineSx, flex: 1, height: '1px' }} />
          <Typography component="span" sx={dimLabelSx}>
            {widthLabel}
          </Typography>
          <Box sx={{ ...dimLineSx, flex: 1, height: '1px' }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 28,
              flexShrink: 0,
              py: 0.5,
            }}
          >
            <Box sx={{ ...dimLineSx, width: '1px', flex: 1 }} />
            <Typography
              component="span"
              sx={{
                ...dimLabelSx,
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                my: 0.5,
              }}
            >
              {heightLabel}
            </Typography>
            <Box sx={{ ...dimLineSx, width: '1px', flex: 1 }} />
          </Box>

          <Box
            ref={frameRef}
            className="polc-editor-frame"
            sx={{
              width: previewWidth,
              height: previewHeight,
              position: 'relative',
              bgcolor: '#fff',
              boxShadow: 2,
              flexShrink: 0,
            }}
          >
            <Box
              className="polc-editor-image-area"
              sx={{
                position: 'absolute',
                overflow: 'hidden',
                cursor: 'grab',
                touchAction: 'none',
                ...imageAreaStyle,
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <Box
                component="img"
                src={imageSrc}
                alt=""
                draggable={false}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${imgDisplayW}px`,
                  height: `${imgDisplayH}px`,
                  transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px))`,
                  userSelect: 'none',
                  pointerEvents: 'none',
                  maxWidth: 'none',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 2, maxWidth: previewWidth, mx: 'auto' }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {translate('polaroid_creator.zoom_label')}
        </Typography>
        <Slider
          value={transform?.userScale ?? 1}
          min={1}
          max={3}
          step={0.01}
          onChange={handleZoomChange}
          aria-label={translate('polaroid_creator.zoom_label')}
        />
        <Typography variant="caption" color="text.secondary">
          {translate('polaroid_creator.drag_hint')}
        </Typography>
      </Box>
    </Box>
  );
}
